import { useCallback, useEffect, useState } from "react";
import { Coffee, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const money = (cents, cur) => {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "gbp").toUpperCase() }).format(cents / 100); }
  catch { return `£${(cents / 100).toFixed(2)}`; }
};
const shift = (key, by) => {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

// Visible only to the app owner (server returns 403 for everyone else).
export const TipSummaryCard = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = useCallback((month) => {
    setLoading(true);
    api.get("/me/tips-summary", { params: month ? { month } : {} })
      .then((r) => setData(r.data)).catch(() => { if (!month) setData(null); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const totals = Object.entries(data.totals);
  const navBtn = "h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-30";
  return (
    <div className={`rounded-xl border border-amber-300/70 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30 px-2.5 py-2 transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="tip-summary-card">
      <div className="flex items-center gap-1">
        <Coffee className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300 shrink-0 mr-1" />
        <button onClick={() => load(shift(data.month_key, -1))} className={navBtn} data-testid="tip-summary-prev" aria-label="Previous month"><ChevronLeft className="h-3.5 w-3.5" /></button>
        <div className="flex-1 text-center text-[10px] uppercase tracking-wide text-stone-500" data-testid="tip-summary-month">{data.month} · only you</div>
        <button onClick={() => load(shift(data.month_key, 1))} disabled={data.is_current} className={navBtn} data-testid="tip-summary-next" aria-label="Next month"><ChevronRight className="h-3.5 w-3.5" /></button>
      </div>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 text-left mt-1" data-testid="tip-summary-toggle">
        <div className="flex-1 min-w-0 text-sm font-semibold" data-testid="tip-summary-total">
          {totals.length ? totals.map(([c, v]) => money(v, c)).join(" + ") : money(0, "gbp")}
          <span className="ml-1.5 text-xs font-normal text-stone-500" data-testid="tip-summary-count">from {data.count} tip{data.count === 1 ? "" : "s"}</span>
        </div>
        {data.recent.length > 0 && <ChevronDown className={`h-3.5 w-3.5 text-stone-500 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && data.recent.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-amber-200 dark:border-amber-900 pt-2" data-testid="tip-summary-recent">
          {data.recent.map((r, i) => (
            <li key={`${r.at}-${i}`} className="text-xs flex gap-2">
              <span className="font-medium text-amber-700 dark:text-amber-300 shrink-0">{money(r.amount, r.currency)}</span>
              <span className="truncate text-stone-600 dark:text-stone-300">{r.name || "Anonymous"}{r.message ? ` — ${r.message}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
